<script setup lang="ts">
import { KsButton, KsIcon } from "@aschehoug/kloss";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";

const props = defineProps<{
  variant: SingleProductVariant;
}>();

const auth = useAuthorizeStore();

const { data: previews } = useProductPreviews();

const enablePreview = useEnableProductPreview();

function handlePreviewOrder() {
  enablePreview.mutate(props.variant.sku);
}

const hrColor = computed(() => props.variant.mainColor || null);

const expiry = computed(() => previews.value?.get(props.variant.sku));

const previewLink = computed(() => {
  if (expiry.value && !isPast(expiry.value)) {
    return props.variant.previewLink;
  }

  return null;
});

const now = useNow();

const userIsAllowedToPreview = computed(() => {
  if (expiry.value && isBefore(expiry.value, now.value)) {
    // Preview has already been ordered and expired
    return false;
  }

  return auth.loggedIn && auth.isTeacher && !auth.isConsumer;
});

const contentFlags = computed(() => ({
  unibok: !!props.variant.formats?.includes("unibok"),
  description: !!(props.variant.isPurchasable && props.variant.description),
  preview: !!(props.variant.previewLink && userIsAllowedToPreview.value),
}));

const showDivider = computed(() =>
  Object.values(contentFlags.value).some((it) => it),
);
</script>

<template>
  <section class="mt-[--outer-padding] flex flex-col gap-5 bg-white/70 p-[--outer-padding] text-xl">
    <div class="cover mx-auto flex w-full flex-col gap-4 text-center">
      <!-- FIXME: These are 99% from Biblio, which is uncached. We should consider building a proxy, so that we can cache them -->
      <CrystallizeImage v-if="variant.cover?.url" :image="variant.cover" />
      <div v-else class="placeholder aspect-[3/4] w-full" />
      <a
        v-if="variant.browsableLink"
        :href="variant.browsableLink"
        target="_blank"
        class="text-[smaller]"
      >
        Bla i boken
      </a>
    </div>
    <div class="details flex flex-col gap-8">
      <UiProductsDetails :variant="variant" />
      <hr v-if="showDivider" />
      <UiAboutUnibok
        v-if="contentFlags.unibok"
        class="text-base font-normal"
        :prefix="true"
        :title="variant.title?.replace(/unibok/i, '').trim()"
      />
      <div
        v-else-if="contentFlags.description"
        class="text-base font-normal"
        v-html="variant.description"
      />

      <div v-if="contentFlags.preview" class="flex flex-col gap-3 text-sm">
        <b class="text-lg font-semibold">Vil du vurdere boka?</b>
        <template v-if="previewLink">
          <p class="leading-normal">
            Du har tilgang til et digitalt vurderingseksemplar av boka.
          </p>
          <a
            :href="previewLink"
            target="_blank"
            rel="noopener"
            class="flex w-fit items-center gap-[0.4rem]"
          >
            Les vurderingseksemplar
            <KsIcon :icon="faExternalLink" :scale="0.6" />
          </a>
        </template>
        <template v-else>
          <p class="leading-normal">
            Du kan få tilgang til et digitalt vurderingseksemplar av boka.
            Vurderingseksamplaret vil være tilgjengelig i tre måneder etter at
            det er aktivert.
          </p>
          <KsButton
            variant="border"
            shape="rounded"
            size="small"
            class="w-fit !border border-black-80 text-sm font-normal [--ks-border:theme(colors.black.80)]"
            @click="handlePreviewOrder"
          >
            Aktiver vurderingseksemplar
          </KsButton>
        </template>
      </div>

      <UiSingleProductCta :variant="variant" />
    </div>
  </section>
</template>

<style scoped lang="scss">
.placeholder {
  background: rgba(var(--variant-background), 0.2);
}

section {
  @media screen and (min-width: $small) {
    --img-max-width: 15.625rem;
    display: grid;
    gap: pixelsToRem(60);
    grid-template-columns: var(--img-max-width) minmax(0, 1fr);

    .cover {
      max-width: var(--img-max-width);
    }
  }

  .details {
    --inner-padding: 0.75rem;
    padding: var(--inner-padding);

    @media screen and (min-width: $small) {
      --inner-padding: 0;
    }
  }

  hr {
    --hr-color: v-bind(hrColor);
    border: none;
    border-top: 1px solid var(--hr-color, rgb(var(--variant-background)));
  }
}
</style>
